import { useEffect, useRef, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { useCopy } from '@floorfile/i18n'
import { Button } from '@floorfile/ui'
import { componentsCopy } from './copy'

/**
 * Tells the operator when a deploy has landed under their feet.
 *
 * The PWA worker uses `skipWaiting` + `clientsClaim`, so a new build takes
 * control of already-open tabs immediately and `cleanupOutdatedCaches` deletes
 * the precache those tabs are still running from. From that moment the tab is
 * on a dead build: its remaining lazy routes exist neither in the cache nor on
 * the server. `lib/lazy-route.ts` recovers automatically if they hit one, but
 * that costs them a reload mid-task with no warning.
 *
 * This surfaces the same condition BEFORE it bites, and deliberately does not
 * reload on its own — a floor tablet may be mid-form, and discarding unsaved
 * entry to install an update the operator never asked for is the worse
 * failure. The banner is advisory; the tab keeps working for any route already
 * loaded.
 */
export function NewVersionBanner() {
  // Before the early return below: hooks cannot sit behind `if (!updated)`.
  const c = useCopy(componentsCopy).newVersion
  const [updated, setUpdated] = useState(false)
  // `controllerchange` also fires the first time a worker claims a page that
  // loaded without one — a first visit, not an update. Only a change while a
  // controller was ALREADY in place means the build was replaced.
  const hadController = useRef(false)

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return

    hadController.current = navigator.serviceWorker.controller !== null

    function onControllerChange() {
      if (hadController.current) {
        setUpdated(true)
        return
      }
      // Initial claim; any change after this one is a genuine update.
      hadController.current = true
    }

    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange)
    return () => navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange)
  }, [])

  if (!updated) return null

  return (
    <div
      role="status"
      className="fixed inset-x-0 bottom-0 z-50 flex flex-wrap items-center justify-center gap-3 border-t bg-card px-4 py-3 shadow-lg"
    >
      <p className="text-sm text-foreground">{c.message}</p>
      <Button className="inline-flex items-center gap-1.5" onClick={() => window.location.reload()}>
        <RefreshCw className="h-4 w-4" /> {c.reload}
      </Button>
      <Button variant="outline" onClick={() => setUpdated(false)}>
        {c.dismiss}
      </Button>
    </div>
  )
}
